/**
 * User Profile API Service
 * Handles customer profile details, password updates, saved places,
 * bookings/trips, and notifications with real network requests,
 * auth tokens, and offline mock fallbacks.
 */

import { API_CONFIG } from '../config'
import { clearAuthSession } from '../auth/authApi'

export interface ApiResponse<T = any> {
  success: boolean
  message: string
  data?: T
  warning?: string
}

export type UserProfile = {
  name: string
  email: string
  phone: string
  avatar?: string
  has_password: boolean
}

export type ChangePasswordPayload = {
  currentPassword: string
  newPassword: string
  confirmPassword: string
}

export type SavedPlaceSource = 'private-tour' | 'day-trip'

export type SavedPlaceItem = {
  id: string
  source: SavedPlaceSource
  title: string
  duration: string
  price: string
  image: string
  explorePath: string
}

export type TripStatus = 'Confirmed' | 'Upcoming' | 'Cancelled' | 'Completed'

export type UserTrip = {
  id: number
  reference: string
  bookingReference: string
  date: string
  time: string
  from: string
  fromAddress: string
  to: string
  toAddress: string
  price: number
  paid: boolean
  status: TripStatus
  customerName: string
  email: string
  phone: string
  vehicle: string
  passengers: number
  luggage: number
}

export type UserNotificationGroup = 'Today–22 May 2026' | 'Yesterday' | string

export type UserNotification = {
  id: number
  group: UserNotificationGroup
  title: string
  description: string
  time: string
}

export const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '')
export const API_KEY = import.meta.env.VITE_API_KEY || ''

const PROFILE_STORAGE_KEY = 'salzburg_profile_details'
const SAVED_STORAGE_KEY = 'salzburg_saved_places'

export const notifyProfileUpdated = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('customer-profile-updated'))
  }
}

const getAuthHeaders = (): HeadersInit => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(API_KEY ? { 'X-API-Key': API_KEY } : {}),
  }
}

const getPasswordStatus = (value: unknown): boolean | undefined => {
  if (!value || typeof value !== 'object') return undefined
  const source = value as Record<string, unknown>
  const valueFromResponse = source.has_password ?? source.hasPassword
  return typeof valueFromResponse === 'boolean' ? valueFromResponse : undefined
}

const normalizeProfile = (value: unknown): UserProfile | null => {
  if (!value || typeof value !== 'object') return null
  const source = value as Record<string, unknown>
  const firstNameValue = source.first_name ?? source.firstName
  const lastNameValue = source.last_name ?? source.lastName
  const firstName = typeof firstNameValue === 'string' ? firstNameValue.trim() : ''
  const lastName = typeof lastNameValue === 'string' ? lastNameValue.trim() : ''
  const fullName = typeof source.full_name === 'string' ? source.full_name.trim() : ''
  const name = typeof source.name === 'string' ? source.name.trim() : fullName || [firstName, lastName].filter(Boolean).join(' ')
  const email = typeof source.email === 'string' ? source.email : ''
  const phoneValue = source.phone ?? source.phone_number ?? source.phoneNumber
  const phone = typeof phoneValue === 'string' ? phoneValue : ''
  const avatarValue = source.avatar ?? source.profile_image ?? source.profileImage ?? source.profile_photo ?? source.photo ?? source.photo_url ?? source.image_url
  const avatar = typeof avatarValue === 'string' ? avatarValue : undefined
  const hasPassword = getPasswordStatus(value) ?? true

  return { name, email, phone, has_password: hasPassword, ...(avatar ? { avatar } : {}) }
}

const getStoredProfileName = (): string => {
  const storedUser = localStorage.getItem('user_data')
  const user = normalizeProfile(storedUser ? JSON.parse(storedUser) : null)
  if (user?.name) return user.name

  const storedProfile = localStorage.getItem(PROFILE_STORAGE_KEY)
  const profile = normalizeProfile(storedProfile ? JSON.parse(storedProfile) : null)
  return profile?.name || ''
}

const withDisplayNameFallback = (profile: UserProfile): UserProfile => {
  if (profile.name) return profile

  const storedName = getStoredProfileName()
  if (storedName) return { ...profile, name: storedName }

  const email = profile.email || localStorage.getItem('user_email') || ''
  const username = email.split('@')[0]?.trim() || ''
  return { ...profile, name: username }
}

export const getFormattedTodayGroup = (): string => {
  const now = new Date()
  const day = now.getDate()
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const month = monthNames[now.getMonth()]
  const year = now.getFullYear()
  return `Today – ${day} ${month} ${year}`
}

export const getFormattedYesterdayGroup = (): string => {
  const yesterday = new Date()
  yesterday.setDate(yesterday.getDate() - 1)
  const day = yesterday.getDate()
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const month = monthNames[yesterday.getMonth()]
  const year = yesterday.getFullYear()
  return `Yesterday – ${day} ${month} ${year}`
}


// ==========================================
// 1. PROFILE ENDPOINTS
// ==========================================

export async function apiGetUserProfile(): Promise<ApiResponse<UserProfile>> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/user/profile/me`, {
      method: 'GET',
      headers: getAuthHeaders(),
    })

        const result = await response.json().catch(() => null)
    if (response.status === 401) {
      clearAuthSession()
      if (typeof window !== 'undefined') window.location.assign('/')
      return { success: false, message: result?.message || 'Your session has expired. Please log in again.' }
    }

    if (response.ok) {
      const normalized = normalizeProfile(result?.data?.user || result?.user || result?.data || result)
      const data = normalized ? withDisplayNameFallback(normalized) : null
      if (data) {
        localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(data))
        notifyProfileUpdated()
        return { success: true, message: result?.message || 'Profile loaded', data }
      }
    }
    return { success: false, message: result?.message || result?.detail || 'Unable to load profile.' }
  } catch {
    return { success: false, message: `Unable to contact ${API_BASE_URL}.` }
  }
}

export async function apiUpdateUserProfile(
  payload: Partial<UserProfile>,
): Promise<ApiResponse<UserProfile>> {
  try {

    const response = await fetch(`${API_BASE_URL}/api/user/profile`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    })
    const result = await response.json().catch(() => null)

    if (response.status === 401) {
      clearAuthSession()
      if (typeof window !== 'undefined') window.location.assign('/')
      return { success: false, message: result?.message || 'Your session has expired. Please log in again.' }
    }

    if (response.ok) {
      const responseProfile = result?.data?.user || result?.user || result?.data || result
      const normalized = normalizeProfile(responseProfile)
      const stored = localStorage.getItem(PROFILE_STORAGE_KEY)
      const storedProfile = normalizeProfile(stored ? JSON.parse(stored) : null)
      const serverData = normalized
        ? withDisplayNameFallback({
            ...normalized,
            name: normalized.name || payload.name || '',
            has_password: getPasswordStatus(responseProfile) ?? storedProfile?.has_password ?? true,
          })
        : null
      if (!serverData) return { success: false, message: 'Profile update returned invalid data.' }
      localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(serverData))
      notifyProfileUpdated()
      return { success: true, message: result?.message || 'Profile updated successfully', data: serverData }
    }
   return { success: false, message: result?.message || result?.detail || 'Profile update failed.' }
  } catch {
    return { success: false, message: `Unable to contact ${API_BASE_URL}.` }
  }
}

export async function apiUploadUserProfileAvatar(file: File): Promise<ApiResponse<{ avatar: string }>> {
  try {
    const formData = new FormData()
    formData.append('avatar', file)
    const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null
    const response = await fetch(`${API_BASE_URL}/api/user/profile/avatar`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(API_KEY ? { 'X-API-Key': API_KEY } : {}),
      },
      body: formData,
    })
    const result = await response.json().catch(() => null)

    if (response.status === 401) {
      clearAuthSession()
      if (typeof window !== 'undefined') window.location.assign('/')
      return { success: false, message: result?.message || 'Your session has expired. Please log in again.' }
    }
    if (!response.ok) {
      return { success: false, message: result?.message || result?.detail || 'Photo upload failed.' }
    }

    const data = normalizeProfile(result?.data?.user || result?.user || result?.data || result)
    let avatar = data?.avatar
    if (!avatar) {
      const refreshed = await apiGetUserProfile()
      avatar = refreshed.success ? refreshed.data?.avatar : undefined
    }
    if (!avatar) return { success: false, message: 'Photo upload returned no image.' }
    localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify({ ...data, avatar }))
    notifyProfileUpdated()
    return { success: true, message: result?.message || 'Photo uploaded successfully', data: { avatar } }
  } catch {
    return { success: false, message: `Unable to contact ${API_BASE_URL}.` }
  }
}

export async function apiChangeUserPassword(
  payload: ChangePasswordPayload,
): Promise<ApiResponse> {
  try {
     const endpoint = `${API_BASE_URL}/api/user/change-password`
      const response = await fetch(endpoint,  {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    })
    const result = await response.json().catch(() => null)

    if (response.status === 401) {
      clearAuthSession()
      if (typeof window !== 'undefined') window.location.assign('/')
      return { success: false, message: result?.message || 'Your session has expired. Please log in again.' }
    }

    if (response.ok) {
      return { success: true, message: result?.message || 'Password changed successfully' }
    }

    return {
      success: false,
      message: result?.message || result?.detail || result?.error || 'Password update failed',
      warning: result?.warning,
    }
  } catch {
    return {
      success: false,
      message: 'Failed to connect to password update service',
      warning: `Unable to contact ${API_BASE_URL}.`,
    }
  }
}

export async function apiCreateUserPassword(
  payload: Omit<ChangePasswordPayload, 'currentPassword'>,
): Promise<ApiResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/user/create-password`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    })
    const result = await response.json().catch(() => null)

    if (response.status === 401) {
      clearAuthSession()
      if (typeof window !== 'undefined') window.location.assign('/')
      return { success: false, message: result?.message || 'Your session has expired. Please log in again.' }
    }
     if (response.ok) {
      return { success: true, message: result?.message || 'Password created successfully' }
    }

    return {
      success: false,
      message: result?.message || result?.detail || result?.error || 'Password creation failed',
      warning: result?.warning,
    }
    } catch {
    return {
      success: false,
      message: 'Failed to connect to password creation service',
      warning: `Unable to contact ${API_BASE_URL}.`,
    }
  }
}

// ==========================================
// 2. SAVED PLACES ENDPOINTS
// ==========================================

export async function apiGetUserSavedPlaces(): Promise<ApiResponse<SavedPlaceItem[]>> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/user/saved-places`, {
      method: 'GET',
      headers: getAuthHeaders(),
    }).catch(() => null)

    if (response && response.ok) {
      const result = await response.json().catch(() => null)
      const data = result?.data || result
      if (Array.isArray(data)) {
        localStorage.setItem(SAVED_STORAGE_KEY, JSON.stringify(data))
        return { success: true, message: 'Saved places fetched', data }
      }
    }
  } catch {}

  // Fallback to local storage
  try {
    const raw = localStorage.getItem(SAVED_STORAGE_KEY)
    const items = raw ? JSON.parse(raw) : []
    return { success: true, message: 'Saved places loaded from storage', data: items }
  } catch {
    return { success: true, message: 'Saved places ready', data: [] }
  }
}

export async function apiAddUserSavedPlace(item: SavedPlaceItem): Promise<ApiResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/user/saved-places`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(item),
    }).catch(() => null)

    if (response && response.ok) {
      const result = await response.json().catch(() => null)
      return { success: true, message: result?.message || 'Place saved' }
    }
  } catch {}

  return { success: true, message: 'Place saved locally' }
}

export async function apiRemoveUserSavedPlace(id: string): Promise<ApiResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/user/saved-places/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    }).catch(() => null)

    if (response && response.ok) {
      const result = await response.json().catch(() => null)
      return { success: true, message: result?.message || 'Place removed' }
    }
  } catch {}

  return { success: true, message: 'Place removed' }
}

// ==========================================
// 3. MY TRIPS ENDPOINTS
// ==========================================

export async function apiGetUserTrips(): Promise<ApiResponse<UserTrip[]>> {
  const endpoint = `${API_BASE_URL}/api/user/trips`

  try {
    const response = await fetch(endpoint, {
      method: 'GET',
      headers: getAuthHeaders(),
    })

    const result = await response.json().catch(() => null)

    console.log('MY TRIPS URL:', endpoint)
    console.log('MY TRIPS STATUS:', response.status)
    console.log('MY TRIPS RESPONSE:', result)

    if (!response.ok) {
      return {
        success: false,
        message:
          result?.message ||
          result?.detail ||
          `Failed to load trips (${response.status})`,
        data: [],
      }
    }

    // Backend returns:
    // { count, next, previous, results: [...] }
    const bookings = Array.isArray(result?.results)
      ? result.results
      : Array.isArray(result?.data)
        ? result.data
        : Array.isArray(result)
          ? result
          : null

    if (!bookings) {
      console.error('MY TRIPS: Invalid response:', result)

      return {
        success: false,
        message: 'Invalid trips response from server.',
        data: [],
      }
    }

    const statusMap: Record<string, TripStatus> = {
      pending: 'Upcoming',
      confirmed: 'Confirmed',
      upcoming: 'Upcoming',
      cancelled: 'Cancelled',
      canceled: 'Cancelled',
      completed: 'Completed',
    }

    const trips: UserTrip[] = bookings.map((booking: any) => {
      const pickupDate = booking.pickup_datetime
        ? new Date(booking.pickup_datetime)
        : null

      const validDate =
        pickupDate && !Number.isNaN(pickupDate.getTime())

      return {
        id: Number(booking.id),

        reference:
          booking.booking_id ||
          `BKG-${booking.id}`,

        bookingReference:
          booking.booking_id ||
          `BKG-${booking.id}`,

        date: validDate
          ? pickupDate!.toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })
          : '-',

        time: validDate
          ? pickupDate!.toLocaleTimeString('en-GB', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            })
          : '-',

        from:
          booking.pickup_location ||
          booking.location ||
          '-',

        fromAddress:
          booking.pickup_location ||
          booking.location ||
          '-',

        to:
          booking.drop_location ||
          '-',

        toAddress:
          booking.drop_location ||
          '-',

        price: Number(
          booking.final_price ??
          booking.estimated_price ??
          booking.vehicle_price ??
          0
        ),

        paid: false,

        status:
          statusMap[
            String(booking.status || '').toLowerCase()
          ] || 'Upcoming',

        customerName:
          booking.traveler_name ||
          '-',

        email:
          booking.traveler_email ||
          '-',

        phone:
          booking.traveler_phone ||
          '-',

        vehicle:
          booking.vehicle !== null &&
          booking.vehicle !== undefined
            ? String(booking.vehicle)
            : '-',

        passengers:
          Number(booking.passengers) || 0,

        luggage:
          Number(booking.luggage) || 0,
      }
    })

    console.log('MY TRIPS MAPPED:', trips)

    return {
      success: true,
      message: 'Trips fetched successfully.',
      data: trips,
    }
  } catch (error) {
    console.error('MY TRIPS ERROR:', error)

    return {
      success: false,
      message:
        error instanceof Error
          ? error.message
          : 'Unable to load trips.',
      data: [],
    }
  }
}

export async function apiCancelUserTrip(
  tripId: number | string,
  reason: string,
): Promise<ApiResponse> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/user/trips/${tripId}/cancel`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ reason }),
      },
    ).catch(() => null)

    if (response) {
      const result = await response.json().catch(() => null)

      if (response.ok) {
        return {
          success: true,
          message:
            result?.message || 'Trip cancelled successfully',
        }
      }

      return {
        success: false,
        message:
          result?.message || 'Trip cancellation failed',
      }
    }
  } catch {}

  return {
    success: false,
    message: 'Unable to connect to trip cancellation service',
  }
}

// ==========================================
// 4. NOTIFICATIONS ENDPOINTS
// ==========================================

export async function apiGetUserNotifications(): Promise<
  ApiResponse<UserNotification[]>
> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/user/notifications`,
      {
        method: 'GET',
        headers: getAuthHeaders(),
      },
    ).catch(() => null)

    if (response && response.ok) {
      const result = await response.json().catch(() => null)
      const data = result?.data || result

      if (Array.isArray(data)) {
        return {
          success: true,
          message: 'Notifications fetched',
          data,
        }
      }
    }
  } catch {}

  return {
    success: true,
    message: 'Notifications ready',
    data: [],
  }
}

export async function apiClearUserNotifications(): Promise<ApiResponse> {
  if (!API_CONFIG.isRealData) {
    return {
      success: true,
      message: 'Notifications cleared',
    }
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/user/notifications/clear`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
      },
    ).catch(() => null)

    if (response && response.ok) {
      const result = await response.json().catch(() => null)

      return {
        success: true,
        message:
          result?.message || 'Notifications cleared',
      }
    }
  } catch {}

  return {
    success: true,
    message: 'Notifications cleared',
  }
}
